import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, CloudDownload, Database, FileInput, FolderSync, Loader2, RefreshCw, Rocket, TrendingUp, Warehouse } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (value) => (value ? new Date(value).toLocaleString() : '—');

export default function VRMStackImportPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState({ stats: {}, campaigns: { live: [], recent: [], ended: [] } });
  const [importFile, setImportFile] = useState(null);
  const [importingFile, setImportingFile] = useState(false);
  const [importingSheet, setImportingSheet] = useState(false);
  const [formData, setFormData] = useState({
    entity_name: 'vrm_stack_house_data',
    storage_mode: 'hybrid',
    category: '',
    subcategory: '',
    import_notes: '',
    sheet_url: '',
  });

  const getHeaders = () => {
    const token = localStorage.getItem('dme_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/admin/vrm-stack/dashboard`, { headers: getHeaders() });
      setDashboard(res.data || { stats: {}, campaigns: { live: [], recent: [], ended: [] } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load VRM-Stack dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const statCards = useMemo(() => ([
    {
      key: 'total-house-leads',
      label: 'Total House Leads',
      value: dashboard.stats.total_house_leads || 0,
      icon: Warehouse,
      accent: 'text-sky-600 bg-sky-100',
    },
    {
      key: 'forever-resellable',
      label: 'Forever Re-sellable',
      value: dashboard.stats.forever_resellable || 0,
      icon: Rocket,
      accent: 'text-emerald-600 bg-emerald-100',
    },
    {
      key: 'leads-sold',
      label: 'Leads Sold / Fed',
      value: dashboard.stats.sold_count || 0,
      icon: TrendingUp,
      accent: 'text-fuchsia-600 bg-fuchsia-100',
    },
    {
      key: 'campaign-total',
      label: 'Total Campaigns',
      value: dashboard.stats.total_campaigns || 0,
      icon: FolderSync,
      accent: 'text-amber-600 bg-amber-100',
    },
  ]), [dashboard]);

  const updateField = (key, value) => setFormData((prev) => ({ ...prev, [key]: value }));

  const handleFileImport = async () => {
    if (!importFile) {
      toast.error('Choose a CSV or XLSX file first');
      return;
    }

    try {
      setImportingFile(true);
      const payload = new FormData();
      payload.append('file', importFile);
      payload.append('entity_name', formData.entity_name.trim() || 'vrm_stack_house_data');
      payload.append('storage_mode', formData.storage_mode);
      payload.append('category', formData.category.trim());
      payload.append('subcategory', formData.subcategory.trim());
      payload.append('import_notes', formData.import_notes.trim());

      const res = await axios.post(`${API_URL}/api/admin/vrm-stack/import/file`, payload, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Imported ${res.data.rows_processed} rows (${res.data.inserted} inserted, ${res.data.updated} updated)`);
      setImportFile(null);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'VRM-Stack file import failed');
    } finally {
      setImportingFile(false);
    }
  };

  const handleSheetImport = async () => {
    if (!formData.sheet_url.trim()) {
      toast.error('Google Sheet URL is required');
      return;
    }

    try {
      setImportingSheet(true);
      const res = await axios.post(`${API_URL}/api/admin/vrm-stack/import/google-sheet`, {
        sheet_url: formData.sheet_url.trim(),
        entity_name: formData.entity_name.trim() || 'vrm_stack_house_data',
        storage_mode: formData.storage_mode,
        category: formData.category.trim(),
        subcategory: formData.subcategory.trim(),
        import_notes: formData.import_notes.trim(),
      }, { headers: getHeaders() });
      toast.success(`Imported ${res.data.rows_processed} rows from Google Sheets (${res.data.inserted} inserted, ${res.data.updated} updated)`);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'VRM-Stack Google Sheet import failed');
    } finally {
      setImportingSheet(false);
    }
  };

  if (loading) {
    return <div className="flex min-h-[400px] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-sky-600" /></div>;
  }

  return (
    <div className="space-y-8 p-6" data-testid="vrm-stack-import-page">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/lead-intake')} data-testid="vrm-stack-back-button">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold" data-testid="vrm-stack-page-title">VRM-Stack Import</h1>
            <p className="text-muted-foreground">House data import, categorization, and endless resale controls for hopper inventory.</p>
          </div>
        </div>
        <Button variant="outline" onClick={fetchDashboard} data-testid="vrm-stack-refresh-button">
          <RefreshCw className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {statCards.map((item) => {
          const Icon = item.icon;
          return (
            <Card key={item.key} data-testid={`vrm-stack-stat-${item.key}`}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className={`rounded-xl p-3 ${item.accent}`}><Icon className="h-5 w-5" /></div>
                  <div>
                    <p className="text-2xl font-bold">{item.value}</p>
                    <p className="text-sm text-muted-foreground">{item.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <Card data-testid="vrm-stack-import-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><FileInput className="h-4 w-4" /> House Data Import</CardTitle>
              <CardDescription>
                Upload CSV, XLSX, or Google Sheets. All fields stay with the lead. If the header column <code>vrm-stack</code> contains exact <code>yes</code>, the lead is marked forever re-sellable.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Entity Name</Label>
                  <Input value={formData.entity_name} onChange={(e) => updateField('entity_name', e.target.value)} data-testid="vrm-stack-entity-name-input" />
                </div>
                <div>
                  <Label>Storage Mode</Label>
                  <Select value={formData.storage_mode} onValueChange={(value) => updateField('storage_mode', value)}>
                    <SelectTrigger data-testid="vrm-stack-storage-mode-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                      <SelectItem value="database_only">Database Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Category</Label>
                  <Input value={formData.category} onChange={(e) => updateField('category', e.target.value)} placeholder="robots, healthcare, geo_local" data-testid="vrm-stack-category-input" />
                </div>
                <div>
                  <Label>Subcategory</Label>
                  <Input value={formData.subcategory} onChange={(e) => updateField('subcategory', e.target.value)} placeholder="cleaning_bots, npi, costs" data-testid="vrm-stack-subcategory-input" />
                </div>
              </div>

              <div>
                <Label>Import Notes</Label>
                <Textarea value={formData.import_notes} onChange={(e) => updateField('import_notes', e.target.value)} rows={3} placeholder="Optional notes that should travel with the house data import batch" data-testid="vrm-stack-import-notes-textarea" />
              </div>

              <div className="grid gap-4 md:grid-cols-[1fr_auto]">
                <div>
                  <Label>CSV / XLSX File</Label>
                  <Input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setImportFile(e.target.files?.[0] || null)} data-testid="vrm-stack-file-input" />
                </div>
                <div className="flex items-end">
                  <Button onClick={handleFileImport} disabled={importingFile} data-testid="vrm-stack-file-import-button">
                    {importingFile ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Database className="mr-2 h-4 w-4" />} Import File
                  </Button>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-[1fr_auto]">
                <div>
                  <Label>Google Sheet URL</Label>
                  <Input value={formData.sheet_url} onChange={(e) => updateField('sheet_url', e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/..." data-testid="vrm-stack-sheet-url-input" />
                </div>
                <div className="flex items-end">
                  <Button variant="outline" onClick={handleSheetImport} disabled={importingSheet} data-testid="vrm-stack-sheet-import-button">
                    {importingSheet ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CloudDownload className="mr-2 h-4 w-4" />} Import Google Sheet
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="vrm-stack-custom-campaigns-card">
            <CardHeader>
              <CardTitle>Custom Client Campaigns</CardTitle>
              <CardDescription>House data stays in the hopper forever, then feeds selected client campaigns when you decide to route it.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg border p-3"><div className="text-muted-foreground">Active</div><div className="text-xl font-semibold">{dashboard.stats.active_campaign_count || 0}</div></div>
                <div className="rounded-lg border p-3"><div className="text-muted-foreground">Total</div><div className="text-xl font-semibold">{dashboard.stats.total_campaigns || 0}</div></div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => navigate('/admin/campaigns')} data-testid="vrm-stack-open-campaigns-button">Manage Campaigns</Button>
                <Button variant="outline" onClick={() => navigate('/admin/business-leads')} data-testid="vrm-stack-open-business-leads-button">Open Business Leads</Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          {[
            { key: 'live', title: 'Live Campaigns', rows: dashboard.campaigns.live },
            { key: 'recent', title: 'Recent Campaigns', rows: dashboard.campaigns.recent },
            { key: 'ended', title: 'Ended Campaigns', rows: dashboard.campaigns.ended },
          ].map((section) => (
            <Card key={section.key} data-testid={`vrm-stack-${section.key}-campaigns-card`}>
              <CardHeader>
                <CardTitle>{section.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Updated</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {section.rows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-muted-foreground">No campaigns yet.</TableCell>
                        </TableRow>
                      ) : section.rows.map((campaign) => (
                        <TableRow key={campaign.id} data-testid={`vrm-stack-${section.key}-campaign-${campaign.id}`}>
                          <TableCell className="font-medium">{campaign.name}</TableCell>
                          <TableCell>{campaign.client_name || 'Unknown'}</TableCell>
                          <TableCell>{campaign.lead_type || '—'}</TableCell>
                          <TableCell>{formatDate(campaign.updated_at || campaign.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}